import moment from "moment";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useRouterCore } from "../../../cores/router";
import { useMeCore } from "../../../cores/me";
import { useEmailMarketingCore } from "../../../cores/emailMarketing";
import Content from "../../layout/Content";
import Layout from "../../layout/Layout";
import "./EmailMarketing.scss";

export const EmailMarketingCampaignDetailScreen = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const intl = useIntl();
  const { company } = useMeCore();
  const { getCampaign, deleteCampaign } = useEmailMarketingCore();
  const { goToEmailMarketingEmails, history, routes } = useRouterCore();
  const [campaign, setCampaign] = useState<Awaited<ReturnType<typeof getCampaign>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!company?.id || !campaignId) {
      return;
    }
    getCampaign(company.id, campaignId).then((data) => {
      setCampaign(data);
      setLoading(false);
    });
  }, [company?.id, campaignId, getCampaign]);

  useEffect(() => {
    if (!campaign || loading || !campaignId) {
      return;
    }
    if (campaign.status === "draft" || campaign.status === "scheduled") {
      setRedirecting(true);
      history.replace(
        `${routes.emailMarketingEmails}?edit=${encodeURIComponent(campaignId)}`
      );
    }
  }, [campaign, loading, campaignId, history, routes.emailMarketingEmails]);

  const handleDelete = async () => {
    if (!company?.id || !campaignId || !campaign) return;
    const confirmKey =
      campaign.status === "scheduled"
        ? "app.emailMarketing.emails.actions.deleteScheduledConfirm"
        : "app.emailMarketing.emails.actions.deleteConfirm";
    if (!window.confirm(intl.formatMessage({ id: confirmKey }))) return;
    await deleteCampaign(company.id, campaignId);
    goToEmailMarketingEmails();
  };

  if (loading || redirecting) {
    return (
      <Layout>
        <Content className="email-mkt email-mkt--reports">
          <p className="email-mkt__note">
            <FormattedMessage id="app.analytics.summary.loadingFetching" />
          </p>
        </Content>
      </Layout>
    );
  }

  if (!campaign) {
    return (
      <Layout>
        <Content className="email-mkt email-mkt--reports">
          <section className="email-mkt__section">
            <h2>
              <FormattedMessage id="app.emailMarketing.campaign.notFound" />
            </h2>
            <button
              type="button"
              className="email-mkt__primary-btn"
              onClick={goToEmailMarketingEmails}
            >
              <FormattedMessage id="app.emailMarketing.campaign.backToEmails" />
            </button>
          </section>
        </Content>
      </Layout>
    );
  }

  const isSent = campaign.status === "sent";
  const audienceInfo = campaign.audienceInfo;
  const canDelete = campaign.status === "draft" || campaign.status === "scheduled";

  return (
    <Layout>
      <Content className="email-mkt email-mkt--reports">
        <section className="email-mkt__reports-heading">
          <div>
            <button
              type="button"
              className="email-mkt__ghost-btn email-mkt__back-link"
              onClick={goToEmailMarketingEmails}
            >
              ← <FormattedMessage id="app.emailMarketing.campaign.backToEmails" />
            </button>
            <span className="email-mkt__eyebrow">
              <FormattedMessage id="app.emailMarketing.campaign.reportLabel" />
            </span>
            <h2>{campaign.subject}</h2>
            {isSent && (
              <p>
                <FormattedMessage
                  id="app.emailMarketing.reports.sentSummary"
                  values={{
                    audience: campaign.audience,
                    list: audienceInfo?.listName || campaign.audience,
                    date: campaign.date,
                  }}
                />
              </p>
            )}
          </div>
          <div className="email-mkt__detail-actions">
            {canDelete && (
              <button
                type="button"
                className="email-mkt__danger-btn"
                onClick={handleDelete}
              >
                <FormattedMessage id="app.emailMarketing.emails.actions.delete" />
              </button>
            )}
          </div>
        </section>

        <section className="email-mkt__detail-meta">
          <div className="email-mkt__detail-meta-card">
            <span>
              <FormattedMessage id="app.emailMarketing.editor.audience" />
            </span>
            <strong>{audienceInfo?.listName || campaign.audience}</strong>
            <small>
              {audienceInfo?.contactCount ?? campaign.sentTo}{" "}
              <FormattedMessage id="app.emailMarketing.editor.recipients" />
            </small>
          </div>
          <div className="email-mkt__detail-meta-card">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.fromAddress" />
            </span>
            <strong>{audienceInfo?.fromEmail || "—"}</strong>
            {audienceInfo?.fromName && <small>{audienceInfo.fromName}</small>}
          </div>
          <div className="email-mkt__detail-meta-card">
            <span>
              <FormattedMessage id="app.emailMarketing.emails.status" />
            </span>
            <strong>
              <FormattedMessage id="app.emailMarketing.emails.status.sent" />
            </strong>
          </div>
        </section>

        <section className="email-mkt__report-hero email-mkt__report-hero--sent">
          <div
            className="email-mkt__report-preview email-mkt__report-preview--live"
            dangerouslySetInnerHTML={{
              __html: campaign.htmlContent || "<p>—</p>",
            }}
          />
          <div className="email-mkt__report-totals">
            <div className="email-mkt__report-total">
              <span>
                <FormattedMessage id="app.emailMarketing.reports.totalSent" />
              </span>
              <strong>{campaign.sentTo}</strong>
            </div>
            <div className="email-mkt__report-total">
              <span>
                <FormattedMessage id="app.emailMarketing.reports.metrics.uniqueOpens" />
              </span>
              <strong>{campaign.openedCount}</strong>
            </div>
            <div className="email-mkt__report-total">
              <span>
                <FormattedMessage id="app.emailMarketing.reports.metrics.uniqueClicks" />
              </span>
              <strong>{campaign.clickedCount}</strong>
            </div>
          </div>
        </section>

        <div className="email-mkt__report-metrics">
          <div className="email-mkt__report-metric">
            <span>
              <FormattedMessage id="app.emailMarketing.reports.metrics.uniqueOpens" />
            </span>
            <strong>{campaign.opened}</strong>
            <small>{campaign.openedCount}</small>
          </div>
          <div className="email-mkt__report-metric">
            <span>
              <FormattedMessage id="app.emailMarketing.reports.metrics.uniqueClicks" />
            </span>
            <strong>{campaign.clicked}</strong>
            <small>{campaign.clickedCount}</small>
          </div>
          <div className="email-mkt__report-metric">
            <span>
              <FormattedMessage id="app.emailMarketing.reports.bounces" />
            </span>
            <strong>{campaign.bounced}</strong>
            <small>{campaign.bouncedCount}</small>
          </div>
          <div className="email-mkt__report-metric">
            <span>
              <FormattedMessage id="app.emailMarketing.reports.complaints" />
            </span>
            <strong>{campaign.complaints}</strong>
            <small>{campaign.complaintsCount}</small>
          </div>
        </div>

        {campaign.sendLogs && campaign.sendLogs.length > 0 && (
          <section className="email-mkt__section">
            <h3>
              <FormattedMessage id="app.emailMarketing.campaign.sendHistory" />
            </h3>
            <div className="email-mkt__send-log-table">
              <div className="email-mkt__send-log-row email-mkt__send-log-row--head">
                <span>
                  <FormattedMessage id="app.sendgridBilling.col.date" />
                </span>
                <span>
                  <FormattedMessage id="app.sendgridBilling.col.type" />
                </span>
                <span>
                  <FormattedMessage id="app.sendgridBilling.col.recipients" />
                </span>
                <span>
                  <FormattedMessage id="app.sendgridBilling.col.from" />
                </span>
              </div>
              {campaign.sendLogs.map((log) => (
                <div className="email-mkt__send-log-row" key={log.id}>
                  <span>{moment(log.createdAt).format("DD MMM YYYY, HH:mm")}</span>
                  <span>{log.sendType}</span>
                  <span>{log.recipientCount}</span>
                  <span>{log.fromEmail}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </Content>
    </Layout>
  );
};
